import { useEffect, useRef, useState } from 'react';

import { Alert } from '@clay-run/glaze/alert';
import { ComboboxOption } from '@clay-run/glaze/combobox';
import { ComboboxField } from '@clay-run/glaze/combobox-field';

import type { Campaign } from '../../_shared/launcher-campaign.types';

/**
 * terra-qyt.15 (U10) — the shared presentational campaign combobox, split out
 * of `GenerateCampaignPicker.tsx` (U9) so the playbook-mode seed-campaign
 * card (`QualifyStep.tsx`'s `ContextCard`) can reuse the exact same picker
 * rather than a second hand-rolled `ComboboxField`. `GenerateCampaignPicker`
 * is now a thin store-bound wrapper around this component; this component
 * owns nothing about the AI-generate session or any other store — it is a
 * plain controlled `campaigns` + `value`/`onChange` combobox.
 *
 * Owns the `inputValue` sync effect `GenerateCampaignPicker.tsx:80-112` used
 * to own directly: the inline `ComboboxField` variant keeps its visible text
 * entirely on `inputValue`/`onInputValueChange` — selecting an option (or a
 * caller setting `value` directly, e.g. a preset-seed effect) does not by
 * itself change what the input displays. This effect is what actually shows
 * the resolved label: it fires whenever `value`'s id changes to a NEW id, and
 * writes that campaign's label into the input once `campaigns` has loaded far
 * enough to resolve it — `lastSyncedIdRef` is only updated once resolution
 * actually succeeds, so a `value` that arrives before `campaigns` has loaded
 * keeps retrying on every `campaigns` change until it resolves, rather than
 * silently settling for a blank input. It never overwrites the rep's own
 * in-progress typing: while `value` is still `null` (nothing chosen yet),
 * this effect's `id === null` branch only clears the input, and only if IT
 * was the one that set the LAST non-empty value.
 */

/** `"${name} · ${type} · ${event_date}"`, dropping the date segment when the campaign has none. */
export function campaignLabel(campaign: Campaign): string {
  const parts = [campaign.name, campaign.type];
  if (campaign.event_date) parts.push(campaign.event_date);
  return parts.join(' · ');
}

export interface CampaignComboboxProps {
  campaigns: Campaign[];
  value: Campaign | null;
  onChange: (campaign: Campaign | null) => void;
  loading: boolean;
  isError: boolean;
  ariaLabel: string;
  placeholder: string;
}

export function CampaignCombobox({
  campaigns,
  value,
  onChange,
  loading,
  isError,
  ariaLabel,
  placeholder,
}: CampaignComboboxProps) {
  const [query, setQuery] = useState(() => (value ? campaignLabel(value) : ''));
  const lastSyncedIdRef = useRef<string | null>(value?.id ?? null);

  useEffect(() => {
    const id = value?.id ?? null;
    if (id === null) {
      if (lastSyncedIdRef.current !== null) {
        lastSyncedIdRef.current = null;
        setQuery('');
      }
      return;
    }
    if (id === lastSyncedIdRef.current) return;
    const campaign = campaigns.find((c) => c.id === id) ?? null;
    if (campaign === null) return;
    lastSyncedIdRef.current = id;
    setQuery(campaignLabel(campaign));
  }, [value, campaigns]);

  if (isError) {
    return <Alert status="error">Failed to load campaigns. Please try again.</Alert>;
  }

  const trimmed = query.trim().toLowerCase();
  const filtered = trimmed === '' ? campaigns : campaigns.filter((c) => c.name.toLowerCase().includes(trimmed));

  return (
    <ComboboxField
      aria-label={ariaLabel}
      placeholder={placeholder}
      items={filtered}
      itemToStringLabel={campaignLabel}
      value={value}
      onValueChange={onChange}
      inputValue={query}
      onInputValueChange={setQuery}
      by="id"
      loading={loading}
    >
      {filtered.map((campaign) => (
        <ComboboxOption key={campaign.id} value={campaign}>
          {campaignLabel(campaign)}
        </ComboboxOption>
      ))}
    </ComboboxField>
  );
}
